/**
 * Ad-platform conversion tracking (Google Ads + Meta Pixel) for fuel-plan
 * completion. Framework-free (like `planGenerationBus`) and initialised once
 * from `main.tsx`; every external touch (script injection, gtag/fbq calls,
 * localStorage) is guarded so tracking can never break the app.
 *
 * WHAT COUNTS AS A CONVERSION — the first fuel box of each journey:
 * a `completed` bus event whose build was dispatched IN THIS BROWSER with
 * `source: "box", dropIndex: 0` (or a future `source: "generate"`), fired at
 * most once per `journeyId`.
 *
 * WHY TWO-EVENT PAIRING (eligibility recorded at `started`, fired at
 * `completed`): event meta is only trustworthy on `started` in the
 * dispatching tab — the provider publishes from a session-local `metaRef`,
 * so notification-drain deliveries in a fresh tab and bare re-`track()`s
 * from pending-screen deep links publish with EMPTY meta. Filtering on the
 * `completed` event's own meta would either miss real conversions or
 * false-fire on drain-replayed later boxes/kits. Eligibility lives in
 * localStorage so it survives tab close (builds finish while away) and is
 * shared across same-origin tabs (BroadcastChannel fan-out).
 *
 * Deliberately NO `principalKey` filter (despite the bus docs' suggestion):
 * the requestId eligibility set is already browser-scoped and stronger, and
 * a hard principal filter would drop legitimate conversions when an anon
 * user logs in mid-build (`a:…` on started → `c:…` on completed).
 *
 * Base pixels are injected ONLY when the Shopify theme chrome hasn't already
 * loaded them (channel apps / custom pixels install gtag+fbq store-wide on
 * some shops) — the guards make double-loading impossible from our side.
 */

import {
  subscribePlanGenerationEvents,
  type PlanGenerationEvent
} from "./planGenerationBus";

/** Public ad-platform identifiers (they ship in page source on any pixel'd site). */
export type TrackingConfig = {
  googleAdsTagId?: string;
  /** Pre-assembled `AW-…/<conversion label>` for gtag's `send_to`. */
  googleAdsConversionSendTo?: string;
  metaPixelId?: string;
};

/**
 * Hardcoded by choice (Brad, 2026-07-14) — no env plumbing. These are public
 * identifiers, and the campaign tags don't vary per environment. Dev/test
 * stay pixel-free because `main.tsx` only initialises when the app runs
 * inside the storefront shell (`window.__FG_BOOT__` present).
 */
export const CONVERSION_TRACKING_CONFIG: TrackingConfig = {
  googleAdsTagId: "AW-11438456021",
  googleAdsConversionSendTo: "AW-11438456021/Ays1CNPg0c8cENX5o84q",
  metaPixelId: "1223197037707451"
};

type AnalyticsFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: AnalyticsFn;
    fbq?: AnalyticsFn;
    _fbq?: AnalyticsFn;
  }
}

const ELIGIBLE_KEY = "fg:conv:eligible";
const FIRED_KEY_PREFIX = "fg:conv:fired:";
const JOURNEY_KEY_PREFIX = "fg:conv:journey:";
/** Eligibility entries older than this are pruned (build outcomes resolve in minutes). */
const ELIGIBLE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * localStorage with an in-memory fallback (private mode / quota). The fallback
 * is same-tab only, so tab-close-during-build recovery won't fire there —
 * accepted; same-tab completions still dedupe correctly.
 */
const memoryStore = new Map<string, string>();
function storeGet(key: string): string | null {
  try {
    const v = window.localStorage.getItem(key);
    if (v != null) return v;
  } catch {
    // fall through to memory
  }
  return memoryStore.get(key) ?? null;
}
function storeSet(key: string, value: string): void {
  memoryStore.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // memory copy already holds it
  }
}
type EligibleEntry = { journeyId?: string; at: string };
type EligibleMap = Record<string, EligibleEntry>;

function readEligible(): EligibleMap {
  const raw = storeGet(ELIGIBLE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as EligibleMap;
    }
  } catch {
    // corrupted — start fresh
  }
  return {};
}

function writeEligible(map: EligibleMap): void {
  storeSet(ELIGIBLE_KEY, JSON.stringify(map));
}

function pruneEligible(map: EligibleMap, now: number): EligibleMap {
  const next: EligibleMap = {};
  for (const [id, entry] of Object.entries(map)) {
    const at = Date.parse(entry?.at ?? "");
    if (Number.isFinite(at) && now - at < ELIGIBLE_TTL_MS) next[id] = entry;
  }
  return next;
}

/**
 * A `started` event marks its requestId conversion-eligible when it is a
 * journey's FIRST box (`box` + dropIndex 0 + journeyId — box events missing
 * journeyId are defensively excluded; dispatch sites always pass it) or a
 * standalone generate (no client sets it today; honored for when that flow
 * returns — those gate on requestId only since there is no journeyId).
 * Empty/undefined source is NOT eligible: those are deep-link re-`track()`s
 * or drain replays, possibly of later boxes/kits/regenerates.
 */
export function isEligibleStart(event: PlanGenerationEvent): boolean {
  if (event.type !== "started") return false;
  const { source, dropIndex, journeyId } = event.meta ?? {};
  if (source === "generate") return true;
  return source === "box" && dropIndex === 0 && Boolean(journeyId);
}

/**
 * Decides whether a `completed` event fires the conversion, given the
 * eligibility entry recorded at `started` (null = not eligible) and the
 * fired-guard lookups. Pure — all storage reads happen in the caller.
 */
export function shouldFireConversion(
  event: PlanGenerationEvent,
  entry: EligibleEntry | null,
  guards: { requestFired: boolean; journeyFired: boolean }
): boolean {
  if (event.type !== "completed" || !event.fuelPlanId) return false;
  if (!entry) return false;
  if (guards.requestFired) return false;
  if (entry.journeyId && guards.journeyFired) return false;
  return true;
}

let activeTracking: TrackingConfig | null = null;
let unsubscribeBus: (() => void) | null = null;
let awConfigured = false;

function ensureGoogleBase(tracking: TrackingConfig): void {
  if (!tracking.googleAdsTagId) return;
  const w = window;
  const hadGtagScript = Boolean(
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]')
  );
  if (!w.dataLayer) w.dataLayer = [];
  const dataLayer = w.dataLayer;
  const createdStub = typeof w.gtag !== "function";
  if (createdStub) {
    // Must be a `function` (not arrow) — gtag.js consumes `arguments` objects.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments);
    };
  }
  if (!hadGtagScript) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tracking.googleAdsTagId)}`;
    document.head.appendChild(script);
  }
  // `js` only alongside our own stub — the theme's snippet already sent it.
  if (createdStub) w.gtag?.("js", new Date());
  // Register the Ads tag exactly once, even when the theme's gtag was loaded
  // for a different (e.g. GA4) id — conversions need the AW config present.
  if (!awConfigured) {
    awConfigured = true;
    w.gtag?.("config", tracking.googleAdsTagId);
  }
}

function ensureMetaBase(tracking: TrackingConfig): void {
  if (!tracking.metaPixelId) return;
  const w = window;
  const createdStub = typeof w.fbq !== "function";
  if (createdStub) {
    // Standard Meta base snippet, written directly instead of eval'd markup.
    const stub: AnalyticsFn & {
      callMethod?: AnalyticsFn;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    } = function fbqStub(...args: unknown[]) {
      if (stub.callMethod) stub.callMethod(...args);
      else stub.queue?.push(args);
    };
    stub.push = stub;
    stub.loaded = true;
    stub.version = "2.0";
    stub.queue = [];
    w.fbq = stub;
    if (!w._fbq) w._fbq = stub;
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }
  // fbq dedupes repeated inits of the same id, so this is safe when the theme
  // already initialised a pixel (a DIFFERENT theme pixel id simply means both
  // pixels receive subsequent events — intended).
  w.fbq?.("init", tracking.metaPixelId);
  if (createdStub) w.fbq?.("track", "PageView");
}

function fireConversion(event: PlanGenerationEvent): void {
  const tracking = activeTracking;
  if (!tracking) return;
  // requestId doubles as the platform-side dedupe key (Google transaction_id /
  // Meta eventID) — the backstop if two tabs race the localStorage guards.
  try {
    if (tracking.googleAdsConversionSendTo && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: tracking.googleAdsConversionSendTo,
        transaction_id: event.requestId
      });
    }
  } catch {
    // never let tracking failures surface
  }
  try {
    // Meta requires BOTH an enabled config (metaPixelId) and a usable fbq —
    // a Google-only config must not push events onto a theme-loaded pixel.
    if (tracking.metaPixelId && typeof window.fbq === "function") {
      window.fbq("track", "CompleteRegistration", {}, { eventID: event.requestId });
    }
  } catch {
    // never let tracking failures surface
  }
}

/**
 * Fire a custom (non-conversion) event on both platforms — gtag `event` +
 * Meta `trackCustom` — under the same guards as `fireConversion`: only once
 * tracking is initialised, per-platform config + usable global, never throws.
 * Params must stay PII-free (enums only).
 */
function fireCustomEvent(name: string, params: Record<string, string> = {}): void {
  const tracking = activeTracking;
  if (!tracking) return;
  try {
    if (tracking.googleAdsTagId && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch {
    // never let tracking failures surface
  }
  try {
    if (tracking.metaPixelId && typeof window.fbq === "function") {
      window.fbq("trackCustom", name, params);
    }
  } catch {
    // never let tracking failures surface
  }
}

/** Onboarding wizard opened (funnel top for ad optimisation). */
export function trackOnboardingStart(): void {
  fireCustomEvent("onboarding_start");
}

/** Onboarding wizard finished, by path (`race` | `no_race`). */
export function trackOnboardingFinish(path: "race" | "no_race" | null): void {
  fireCustomEvent("onboarding_complete", path ? { path } : {});
}

function handleEvent(event: PlanGenerationEvent): void {
  try {
    if (event.type === "started") {
      if (!isEligibleStart(event)) return;
      const map = pruneEligible(readEligible(), Date.now());
      map[event.requestId] = {
        ...(event.meta.journeyId ? { journeyId: event.meta.journeyId } : {}),
        at: new Date().toISOString()
      };
      writeEligible(map);
      return;
    }
    if (event.type !== "completed") return;
    const map = readEligible();
    const entry = map[event.requestId] ?? null;
    const fire = shouldFireConversion(event, entry, {
      requestFired: storeGet(FIRED_KEY_PREFIX + event.requestId) != null,
      journeyFired: entry?.journeyId
        ? storeGet(JOURNEY_KEY_PREFIX + entry.journeyId) != null
        : false
    });
    // A settled requestId never fires again — drop its eligibility either way
    // (fired, journey already converted, or missing fuelPlanId) so the map
    // stays small and replays are trivially idempotent.
    if (entry) {
      delete map[event.requestId];
      writeEligible(map);
    }
    if (!fire) return;
    // Guards are written BEFORE the pixel calls so a second delivery of the
    // same completion (same-tab CustomEvent + cross-tab BroadcastChannel +
    // notification-drain replay) can't interleave ahead of them.
    storeSet(FIRED_KEY_PREFIX + event.requestId, new Date().toISOString());
    if (entry?.journeyId) {
      storeSet(JOURNEY_KEY_PREFIX + entry.journeyId, new Date().toISOString());
    }
    fireConversion(event);
  } catch {
    // never let tracking failures surface
  }
}

/**
 * Idempotent boot hook — `main.tsx` calls it with `CONVERSION_TRACKING_CONFIG`,
 * and ONLY when the app runs inside the storefront shell (`window.__FG_BOOT__`)
 * and isn't preview-gated, so Vite dev / tests / gated traffic stay pixel-free.
 * No-ops when the config enables no platform. The subscription guard also
 * protects against Vite HMR double-inits.
 */
export function initConversionTracking(tracking: TrackingConfig | undefined): void {
  if (typeof window === "undefined") return;
  if (!tracking || (!tracking.googleAdsTagId && !tracking.metaPixelId)) return;
  if (unsubscribeBus) return;
  activeTracking = tracking;
  try {
    ensureGoogleBase(tracking);
  } catch {
    // script injection failed (CSP, etc.) — listener still installs; the
    // fire path re-checks for a usable gtag/fbq at conversion time.
  }
  try {
    ensureMetaBase(tracking);
  } catch {
    // as above
  }
  unsubscribeBus = subscribePlanGenerationEvents(handleEvent);
}

/** Test-only: unwind module state between cases. */
export function resetConversionTrackingForTests(): void {
  unsubscribeBus?.();
  unsubscribeBus = null;
  activeTracking = null;
  awConfigured = false;
  memoryStore.clear();
}
